import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'app/environments/environment';
import { ApiResponse } from 'app/models/api-response';
import { PaginatedResult } from 'app/models/business-setting';
import { PaginationParams, RoleDto, RoleDetailsDto, CreateRoleDto, UpdateRoleDto, PermissionDto, AssignPermissionsDto, RemovePermissionsDto, RoleUserDto } from 'app/models/role';


@Injectable({
  providedIn: 'root'
})
export class RolesService {

  
  private get apiUrl(): string {
    const base = localStorage.getItem('apiURL');
    if (!base) throw new Error('apiURL not ready');
    return base + "/api/admin";
  }

  constructor(private http: HttpClient) { }

  // ============================================================
  // Roles
  // ============================================================

  // GET: api/admin/roles?pageNumber=1&pageSize=10&search=
  getAll(params: PaginationParams = {}): Observable<ApiResponse<PaginatedResult<RoleDto>>> {
    return this.http.get<ApiResponse<PaginatedResult<RoleDto>>>(
      `${this.apiUrl}/roles`,
      { params: this.buildParams(params) }
    );
  }

  // GET: api/admin/roles/{id}
  getById(id: number): Observable<ApiResponse<RoleDetailsDto>> {
    return this.http.get<ApiResponse<RoleDetailsDto>>(`${this.apiUrl}/roles/${id}`);
  }

  // POST: api/admin/roles
  create(dto: CreateRoleDto): Observable<ApiResponse<RoleDto>> {
    return this.http.post<ApiResponse<RoleDto>>(`${this.apiUrl}/roles`, dto);
  }

  // PUT: api/admin/roles/{id}
  update(id: number, dto: UpdateRoleDto): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/roles/${id}`, dto);
  }

  // DELETE: api/admin/roles/{id}
  delete(id: number): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/roles/${id}`);
  }

  // ============================================================
  // Role Permissions
  // ============================================================

  // GET: api/admin/roles/{id}/permissions
  getRolePermissions(id: number, params: PaginationParams = {}): Observable<ApiResponse<PaginatedResult<PermissionDto>>> {
    return this.http.get<ApiResponse<PaginatedResult<PermissionDto>>>(
      `${this.apiUrl}/roles/${id}/permissions`,
      { params: this.buildParams(params) }
    );
  }

  // POST: api/admin/roles/{id}/permissions (merge)
  assignPermissions(id: number, dto: AssignPermissionsDto): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/roles/${id}/permissions`, dto);
  }

  // PUT: api/admin/roles/{id}/permissions (sync)
  syncPermissions(id: number, dto: AssignPermissionsDto): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/roles/${id}/permissions`, dto);
  }

  // DELETE: api/admin/roles/{id}/permissions
  removePermissions(id: number, dto: RemovePermissionsDto): Observable<ApiResponse<any>> {
    return this.http.request<ApiResponse<any>>(
      'delete',
      `${this.apiUrl}/roles/${id}/permissions`,
      { body: dto }
    );
  }

  // ============================================================
  // Permissions (Global)
  // ============================================================

  // GET: api/admin/permissions
  getAllPermissions(params: PaginationParams = {}): Observable<ApiResponse<PaginatedResult<PermissionDto>>> {
    return this.http.get<ApiResponse<PaginatedResult<PermissionDto>>>(
      `${this.apiUrl}/permissions`,
      { params: this.buildParams(params) }
    );
  }

  // ============================================================
  // Role Users
  // ============================================================

  // GET: api/admin/roles/{id}/users
  getRoleUsers(id: number, params: PaginationParams = {}): Observable<ApiResponse<PaginatedResult<RoleUserDto>>> {
    return this.http.get<ApiResponse<PaginatedResult<RoleUserDto>>>(
      `${this.apiUrl}/roles/${id}/users`,
      { params: this.buildParams(params) }
    );
  }

  // ============================================================
  // Helper
  // ============================================================

  private buildParams(params: PaginationParams): HttpParams {
    let httpParams = new HttpParams();

    if (params.pageNumber) httpParams = httpParams.set('pageNumber', params.pageNumber.toString());
    if (params.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
    if (params.search && params.search.trim()) httpParams = httpParams.set('search', params.search.trim());

    return httpParams;
  }
}